import { add, format, startOfDay, sub } from '@sequencehq/utils/dates'
import { Phase, Quote, ResolvedPhase } from 'Cube/domain/cube.domain.types'
import { parseBillingSchedulePhaseDuration } from '@sequencehq/utils'
import { getOriginalPhaseRunningOn } from 'modules/Cube/domain/postActionStages/queries/getOriginalPhaseRunningOn'

const API_DATE_FORMAT = 'yyyy-MM-dd'

type ShiftablePhase = Pick<ResolvedPhase, 'id' | 'dates'>

/**
 * The phase durations that move the effective date without rebuilding the
 * quote, so any pricing edits are kept. That only works while the date stays
 * inside the original phase the amendment split, and inside the Amendment
 * phase. Otherwise this is undefined, and the phases have to be rebuilt.
 *
 *   original   |------------ B ------------|
 *   before     |--- B ---|--- Amendment ---|
 *   after      |----- B -----|- Amendment -|
 *                            ^ newEffectiveFrom
 */
export const getEffectiveFromShift = ({
  orderedPhases,
  amendment,
  newEffectiveFrom
}: {
  orderedPhases: ShiftablePhase[]
  amendment: NonNullable<Quote['amendment']>
  newEffectiveFrom: Date
}): Record<Phase['id'], Pick<Phase, 'duration'>> | undefined => {
  const effectiveFrom = toApiDate(amendment.effectiveFrom)
  const newDate = toApiDate(newEffectiveFrom)

  const originalPhase = getOriginalPhaseRunningOn({
    original: amendment.original,
    date: amendment.effectiveFrom
  })

  if (!originalPhase) {
    return
  }

  // On the phase's first day there's nothing to split, so there's no phase
  // before the Amendment one to grow or shrink.
  const isSplitBothSides =
    originalPhase.startDate < effectiveFrom && originalPhase.startDate < newDate
  const isInOriginalPhase =
    !originalPhase.endDate || newDate <= originalPhase.endDate

  if (!isSplitBothSides || !isInOriginalPhase) {
    return
  }

  const splitPhase = orderedPhases.find(
    phase => getStartDate(phase) === originalPhase.startDate
  )
  const amendmentPhase = orderedPhases.find(
    phase => getStartDate(phase) === effectiveFrom
  )
  const splitPhaseStart = splitPhase?.dates.absolute.start
  const splitPhaseEnd = splitPhase?.dates.absolute.end

  if (!splitPhase || !splitPhaseStart || !splitPhaseEnd || !amendmentPhase) {
    return
  }

  const isNextToAmendmentPhase =
    toApiDate(add(splitPhaseEnd, { days: 1 })) === effectiveFrom

  if (!isNextToAmendmentPhase) {
    return
  }

  const amendmentPhaseEnd = amendmentPhase.dates.absolute.end

  if (amendmentPhaseEnd && newDate > toApiDate(amendmentPhaseEnd)) {
    return
  }

  const newStartDate = startOfDay(newEffectiveFrom)

  return {
    [splitPhase.id]: {
      duration: parseBillingSchedulePhaseDuration({
        startDate: splitPhaseStart,
        endDate: sub(newStartDate, { days: 1 })
      })
    },
    // An open ended Amendment phase has no end to keep, so it's left as it is
    ...(amendmentPhaseEnd && {
      [amendmentPhase.id]: {
        duration: parseBillingSchedulePhaseDuration({
          startDate: newStartDate,
          endDate: amendmentPhaseEnd
        })
      }
    })
  }
}

const toApiDate = (date: Date) => format(date, API_DATE_FORMAT)

const getStartDate = (phase: ShiftablePhase) =>
  phase.dates.absolute.start && toApiDate(phase.dates.absolute.start)
